import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { ProdutividadeMetasComponent } from "./produtividade-metas/produtividade-metas.component";
import { ProdutividadeTurnosComponent } from "./produtividade-turnos/produtividade-turnos.component";
import { ListarOperacoesComponent } from './operacoes/listar-operacoes/listar-operacoes.component';
import { InformacoesProdutividadeMetaGrafico } from 'app/interfaces/Meta';
import { ProdutividadePorTurnoGrafico, InformacoesProdutividadeTurno } from 'app/interfaces/Turno';
import { finalize } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SidebarService } from 'app/services/sidebar/sidebar.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AutonomiaInsumosComponent } from './autonomia-insumos/autonomia-insumos.component';
import { MapaService } from 'app/services/mapa/mapa.service';
import { CompartilhamentoService } from 'app/services/compartilhamento/compartilhamento.service';
import { Operacao, OperacaoPusher, Operacoes } from 'app/interfaces/Operacao';
import { ClienteService } from 'app/services/cliente/cliente.service';
import { EquipamentosAutonomiasInsumos, AutonomiasPusher } from 'app/interfaces/AutonomiaInsumo';
import { TranslocoModule } from '@jsverse/transloco';
import { OperacaoStatus } from 'app/constants/operacao-status.constants';

@Component({
  selector: 'app-dialog-informacoes',
  standalone: true,
  providers: [],
  imports: [CommonModule, ProdutividadeMetasComponent, ProdutividadeTurnosComponent, ListarOperacoesComponent, MatTooltipModule, MatProgressSpinnerModule, AutonomiaInsumosComponent, TranslocoModule],
  templateUrl: './dialog-informacoes.component.html',
  styleUrl: './dialog-informacoes.component.css'
})

export class DialogInformacoesComponent implements OnInit, OnDestroy, OnChanges {
  public estaExpandido: boolean = false;
  public equipamentoSelecionado: boolean = false;
  public operacaoSelecionada: boolean = false;
  public alturaContainerEquipamentosExpandido: number = 40;
  public alturaContainerOperacoesExpandido: number = 30.5;
  public loading: boolean = true;
  public dadosTurnos: ProdutividadePorTurnoGrafico[] = [];
  public autonomiasInsumos: EquipamentosAutonomiasInsumos = [];
  public operacoes: Operacao[] = [];
  public metaAtual: number = 0;
  public metaMaxima: number = 0;
  public metaEsperada: number = 0;
  public sidebarExpandido: boolean = false;
  private destroy$ = new Subject<void>();
  private contadorLoading: number = 0;
  
  @Input({required:true}) dataInicio!: string;
  @Input({required:true}) dataFim!: string;
  @Input({required:true}) clienteId!: number;
  @Input() atualizacaoAutonomiaInsumo!: AutonomiasPusher;
  @Input() alteracaoEstadoOperacao!: OperacaoPusher;
  @Input() legendaMapaAberto!: boolean;
  @ViewChild('spanExpandido') spanTextoExpandido!: ElementRef<HTMLSpanElement>;

  constructor(
    private sidebarService: SidebarService,
    private clienteService: ClienteService,
    private mapaService: MapaService,
    private compartilhamentoService: CompartilhamentoService
  ) { }

  ngOnInit(): void {
    this.carregarOperacoes();
    if (this.dataInicio && this.dataFim) {
      this.carregarMetas();
      this.carregarTurnos();
    }
    this.buscarAutonomiaInsumos();

    this.sidebarService.isExpanded$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isExpanded => {
        this.sidebarExpandido = isExpanded;
        if (isExpanded) {
          this.estaExpandido = false;
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['atualizacaoAutonomiaInsumo']) {
      this.atualizarAutonomiaInsumos(this.atualizacaoAutonomiaInsumo);
    }

    if (changes['clienteId'] && !changes['clienteId'].firstChange) {
      // Recarregar todas as informações quando o cliente muda
      this.carregarOperacoes();
      this.buscarAutonomiaInsumos();
      if (this.dataInicio && this.dataFim) {
        this.carregarMetas();
        this.carregarTurnos();
      }
    }

    if(changes['dataInicio'] || changes['dataFim']) {
      this.carregarOperacoes();
      if (this.dataInicio && this.dataFim) {
        this.carregarMetas();
        this.carregarTurnos();
      }
    }

    if (changes['alteracaoEstadoOperacao']) {
      this.atualizarEstadoOperacao(this.alteracaoEstadoOperacao);
    }
  }

  public atualizarAutonomiaInsumos(atualizacaoAutonomiaInsumo: AutonomiasPusher): void {
    this.autonomiasInsumos = this.autonomiasInsumos.map(equipamento => {
      const equipamentoAtualizado = atualizacaoAutonomiaInsumo.equipamentos.find(e => e.vc_frota === equipamento.vc_frota);

      if (!equipamentoAtualizado) {
        return equipamento;
      }

      return {
        ...equipamento,
        autonomias: equipamento.autonomias.map(autonomia => {
          const novaAutonomia = equipamentoAtualizado.autonomias.find(a => a.en_tipo_insumo === autonomia.en_tipo_insumo);
          return novaAutonomia ? { ...autonomia, ...novaAutonomia } : autonomia;
        })
      };
    });
  }

  public atualizarEstadoOperacao(alteracaoEstadoOperacao: OperacaoPusher): void {
    this.operacoes = (Array.isArray(this.operacoes) ? this.operacoes : []).map(operacao => {
      if (operacao.vc_service_id === alteracaoEstadoOperacao.vc_service_id) {
        return {
          ...operacao,
          en_estado: alteracaoEstadoOperacao.en_estado as OperacaoStatus
        };
      }
      return operacao;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private iniciarLoading(): void {
    this.contadorLoading++;
    this.loading = true;
  }

  public pararLoading(): void {
    this.contadorLoading--;
    if (this.contadorLoading <= 0) {
      this.loading = false;
      this.contadorLoading = 0;
    }
  }

  public carregarMetas(): void {
    if (!this.dataInicio || !this.dataFim) {
      return;
    }
    this.iniciarLoading();
    this.mapaService.buscarMetasFiltradasMapa(this.clienteId, this.dataInicio, this.dataFim)
      .pipe(finalize(() => this.pararLoading()))
      .subscribe({
        next: (resposta: InformacoesProdutividadeMetaGrafico) => {
          if (resposta?.meta) {
            this.metaAtual = resposta.meta.produzido_periodo;
            this.metaMaxima = resposta.meta.meta_total;
            this.metaEsperada = resposta.meta.meta_esperada_periodo;
          }
        },
        error: (error) => {
          this.metaAtual = 0;
        }
      });
  }

  public carregarTurnos(): void {
    if (!this.dataInicio || !this.dataFim) {
      return;
    }
    this.iniciarLoading();
    this.mapaService.buscarTurnosFiltradosMapa(this.clienteId, this.dataInicio, this.dataFim)
      .pipe(finalize(() => this.pararLoading()))
      .subscribe({
        next: (response: InformacoesProdutividadeTurno[]) => {
          if (response && Array.isArray(response)) {
            this.dadosTurnos = response.map((turno, index) => ({
              nome: turno.vc_nome,
              porcetagemProdutividade: turno.produtividade!,
              produtividadeMaxima: this.metaMaxima,
              cor: this.retornarCorTurno(index)
            }));
          } else {
            this.dadosTurnos = [];
          }
        },
        error: (error) => {
          this.dadosTurnos = [];
        }
      });
  }

  public retornarCorTurno(index: number): string {
    const cores: string[] = ['red', 'green', 'blue'];
    return cores[index % cores.length];
  }

  public alternarCardInformacoes(): void {
    const element = this.spanTextoExpandido?.nativeElement;
    element.querySelectorAll('font').forEach(fontTag => fontTag.remove());

    if (!this.sidebarExpandido) {
      this.compartilhamentoService.toggleData(this.estaExpandido);
      this.estaExpandido = !this.estaExpandido;
    }
  }

  public carregarOperacoes(): void {
    this.iniciarLoading();
    this.mapaService.buscarOperacoesFiltradasMapa(this.clienteId)
      .pipe(finalize(() => this.pararLoading()))
      .subscribe({
        next: (response: Operacoes) => {
          this.operacoes = response;
        },
        error: (error) => {
          this.operacoes = [];
        }
      });
  }

  public buscarAutonomiaInsumos(): void {
    this.clienteService.buscarAutonomiasInsumos(this.clienteId)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (autonomiasInsumos: EquipamentosAutonomiasInsumos) => {
          this.autonomiasInsumos = autonomiasInsumos;
        }
      });
  }

  public modificaEquipamentoExpandido(data: boolean): void {
    this.equipamentoSelecionado = data;

    this.alturaContainerEquipamentosExpandido = this.equipamentoSelecionado ? 58.5 : 40;
  }

  public modificaOperacaoSelecionada(data: boolean): void {
    this.operacaoSelecionada = data;

    this.alturaContainerOperacoesExpandido = this.operacaoSelecionada ? 58.5 : 35.5;
  }
}
